import { useState, useEffect, useRef } from "react";
import { createIcons, icons } from "lucide";

// ─── DATA IMPORT ─────────────────────────────────────────────────────────────
// The portfolio data is loaded from portfolio-data.js (same as original).
// Make sure portfolio-data.js is in the same folder as index.html and exposes:
//   navLinks, personalInfo, terminalLines, skillCategories, skillCards,
//   projects, services, contactLinks
// via window globals (or as ES module exports — see portfolio-data.js notes).
const {
  navLinks,
  personalInfo,
  skillCategories,
  skillCards,
  projects,
  services,
  contactLinks,
} = window; // globals injected by portfolio-data.js  (same <script src> pattern)

// ─── LUCIDE ICON HELPER ───────────────────────────────────────────────────────
function LucideIcon({ name, size = 18 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = `<i data-lucide="${name}" width="${size}" height="${size}" stroke-width="1.5"></i>`;
      createIcons({ icons });
    }
  }, [name, size]);
  return <span ref={ref} style={{ display: "inline-flex", alignItems: "center" }} />;
}

// ─── PARTICLE CANVAS ──────────────────────────────────────────────────────────
function ParticleCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let W, H, particles = [];
    let mouseX = 0, mouseY = 0, mouseActive = false;
    let rafId;

    function resize() {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => { mouseX = e.clientX; mouseY = e.clientY; mouseActive = true; };
    const onLeave = () => { mouseActive = false; };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);

    function rand(min, max) { return Math.random() * (max - min) + min; }
    function createParticle() {
      return { x: rand(0, W), y: rand(0, H), vx: rand(-0.15, 0.15), vy: rand(-0.3, -0.06), radius: rand(1, 2.2), alpha: rand(0.1, 0.45), baseAlpha: rand(0.1, 0.45) };
    }
    for (let i = 0; i < 80; i++) particles.push(createParticle());

    function drawLines() {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.strokeStyle = `rgba(0,255,200,${(1 - dist / 120) * 0.08})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    }

    function tick() {
      ctx.clearRect(0, 0, W, H);
      if (mouseActive) {
        const g = ctx.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, 200);
        g.addColorStop(0, "rgba(0,255,200,0.15)");
        g.addColorStop(0.5, "rgba(0,255,200,0.05)");
        g.addColorStop(1, "rgba(0,255,200,0)");
        ctx.fillStyle = g;
        ctx.fillRect(mouseX - 200, mouseY - 200, 400, 400);
      }
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy;
        if (mouseActive) {
          const dx = mouseX - p.x, dy = mouseY - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 200) {
            const force = (1 - dist / 200) * 0.3;
            const angle = Math.atan2(dy, dx);
            p.vx += Math.cos(angle) * force * 0.5;
            p.vy += Math.sin(angle) * force * 0.5;
            p.alpha = p.baseAlpha + (1 - dist / 200) * 0.35;
          } else p.alpha = p.baseAlpha;
        } else p.alpha = p.baseAlpha;
        p.vx *= 0.99; p.vy *= 0.99; p.vy -= 0.01;
        if (p.y < -10) { p.y = H + 10; p.x = rand(0, W); p.alpha = p.baseAlpha; }
        if (p.x < -10) p.x = W + 10;
        if (p.x > W + 10) p.x = -10;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,255,200,${p.alpha})`;
        ctx.fill();
        if (mouseActive) {
          const dx = mouseX - p.x, dy = mouseY - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 200) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.radius + 2, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(0,255,200,${(1 - dist / 200) * p.alpha * 0.5})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      drawLines();
      rafId = requestAnimationFrame(tick);
    }
    tick();

    return () => {
      window.removeEventListener("resize", resize);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none" }} />;
}

// ─── SCROLL REVEAL HOOK ───────────────────────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.12 }
    );
    const observe = () => document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    observe();
    const t = setTimeout(observe, 100);
    return () => { clearTimeout(t); observer.disconnect(); };
  });
}

// ─── NAVBAR ───────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <nav className={scrolled ? "scrolled" : ""}>
      <div className="nav-inner">
        <div className="nav-logo">&lt;<span>Eyad</span> /&gt;</div>
        <ul className="nav-links">
          {navLinks.map((link) => (
            <li key={link.href}><a href={link.href}>{link.label}</a></li>
          ))}
        </ul>
        <a href="#contact" className="nav-cta">Get in Touch</a>
      </div>
    </nav>
  );
}

// ─── TERMINAL ─────────────────────────────────────────────────────────────────
function Terminal() {
  const lines = [
    { type: "cmd", prompt: "❯", text: "node profile.ts" },
    { type: "out", text: "{" },
    { type: "kv", key: "  name", val: `"${personalInfo.name}"`, valClass: "t-str" },
    { type: "kv", key: "  title", val: `"${personalInfo.title}"`, valClass: "t-str" },
    { type: "kv", key: "  experience", val: '"2+ years"', valClass: "t-str" },
    { type: "kv", key: "  uptime", val: '"99%"', valClass: "t-num" },
    { type: "kv", key: "  available", val: "true", valClass: "t-bool" },
    { type: "kv", key: "  stack", val: '["Node.js", "TypeScript", "MongoDB"]', valClass: "t-str" },
    { type: "out", text: "}" },
    { type: "cursor" },
  ];

  return (
    <div className="hero-terminal">
      <div className="terminal-bar">
        <div className="t-dot red" />
        <div className="t-dot yellow" />
        <div className="t-dot green" />
        <div className="terminal-title">profile.ts</div>
      </div>
      <div className="terminal-body">
        {lines.map((l, i) => (
          <div
            key={i}
            className="t-line"
            style={{ animation: `fadeIn 0.3s ease ${i * 0.12 + 0.6}s both`, opacity: 0 }}
          >
            {l.type === "cmd" && (<><span className="t-prompt">{l.prompt}</span><span className="t-cmd">{l.text}</span></>)}
            {l.type === "out" && <span className="t-out">{l.text}</span>}
            {l.type === "kv" && (<><span className="t-key">{l.key}</span><span className="t-out">:&nbsp;</span><span className={l.valClass}>{l.val}</span><span className="t-out">,</span></>)}
            {l.type === "cursor" && (<><span className="t-prompt">❯</span><span className="t-cursor" /></>)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── HERO ─────────────────────────────────────────────────────────────────────
function Hero() {
  const [first, ...rest] = personalInfo.name.split(" ");
  return (
    <section id="home">
      <div className="container">
        <div className="hero-grid">
          <div>
            <div className="hero-badge">Available for Work</div>
            <h1 className="hero-name">
              <span className="first">{first} </span>
              <span className="last">{rest.join(" ")}</span>
            </h1>
            <div className="hero-title-row">
              <div className="title-sep" />
              <div className="hero-title">{personalInfo.title}</div>
            </div>
            <p className="hero-desc">{personalInfo.description}</p>
            <div className="hero-actions">
              <a href="#projects" className="btn-primary">View Projects</a>
              <a href="#contact" className="btn-ghost">Contact Me</a>
            </div>
            <div className="hero-stats">
              {personalInfo.stats.map((s, i) => (
                <div key={i}>
                  <span className="hero-stat-value">{s.value}</span>
                  <span className="hero-stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
          <Terminal />
        </div>
      </div>
    </section>
  );
}

// ─── SKILLS ───────────────────────────────────────────────────────────────────
function Skills() {
  return (
    <section id="skills">
      <div className="container">
        <div className="section-header reveal">
          <span className="section-tag">{"// expertise"}</span>
          <h2 className="section-title">Skills &amp; Technologies</h2>
          <p className="section-subtitle">The tools and technologies I use to build high-performance systems.</p>
        </div>
        <div className="skills-layout">
          {skillCategories.map((cat, i) => (
            <div key={i} className={`skill-cat reveal reveal-delay-${i + 1}`}>
              <div className="skill-cat-header">
                <div className="skill-cat-icon"><LucideIcon name={cat.icon} /></div>
                <div className="skill-cat-title">{cat.title}</div>
              </div>
              <div className="skill-pills">
                {cat.skills.map((s) => <span key={s} className="skill-pill">{s}</span>)}
              </div>
            </div>
          ))}
        </div>
        <div className="skill-cards">
          {skillCards.map((card, i) => (
            <div key={i} className={`skill-card reveal reveal-delay-${(i % 3) + 1}`}>
              <div className="skill-card-icon"><LucideIcon name={card.icon} /></div>
              <div className="skill-card-title">{card.title}</div>
              <div className="skill-card-desc">{card.description}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PROJECT MODAL ────────────────────────────────────────────────────────────
function ProjectModal({ project, onClose }) {
  const [imgIndex, setImgIndex] = useState(0);
  const images = project.images || [project.image];

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setImgIndex((p) => Math.max(0, p - 1));
      if (e.key === "ArrowRight") setImgIndex((p) => Math.min(images.length - 1, p + 1));
    };
    window.addEventListener("keydown", handler);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", handler); };
  }, [onClose, images.length]);

  return (
    <div className="project-modal active" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="project-modal-content">
        <div className="modal-header">
          <h2 className="modal-title">{project.title}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-gallery">
          <img className="gallery-img" src={images[imgIndex]} alt={project.title} onError={(e) => e.target.style.display = "none"} />
          <div className="gallery-nav">
            <button className="gallery-btn" disabled={imgIndex === 0 || images.length <= 1} onClick={() => setImgIndex((p) => p - 1)}>←</button>
            <button className="gallery-btn" disabled={imgIndex === images.length - 1 || images.length <= 1} onClick={() => setImgIndex((p) => p + 1)}>→</button>
          </div>
          <div className="gallery-counter">{imgIndex + 1} / {images.length}</div>
        </div>
        <div className="modal-body">
          <p className="modal-description">{project.description}</p>
          <div className="modal-meta">
            <div className="modal-meta-item">
              <div className="modal-meta-label">{"// Date"}</div>
              <div className="modal-meta-value">{project.date}</div>
            </div>
            <div className="modal-meta-item">
              <div className="modal-meta-label">{"// Stars"}</div>
              <div className="modal-meta-value">{project.stars ? `${project.stars} Stars` : "N/A"}</div>
            </div>
          </div>
          <div className="modal-meta-item">
            <div className="modal-meta-label">{"// Technologies"}</div>
            <div className="modal-tags">
              {project.tags.map((t) => <span key={t} className="modal-tag">{t}</span>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PROJECTS ─────────────────────────────────────────────────────────────────
function Projects() {
  const [activeProject, setActiveProject] = useState(null);
  return (
    <section id="projects">
      <div className="container">
        <div className="section-header reveal">
          <span className="section-tag">{"// work"}</span>
          <h2 className="section-title">Featured Projects</h2>
          <p className="section-subtitle">A selection of systems and tools I've built — from Discord experiences to scalable backends.</p>
        </div>
        <div className="projects-grid">
          {projects.map((p, i) => (
            <div
              key={i}
              className={`project-card reveal reveal-delay-${(i % 3) + 1}`}
              onClick={() => setActiveProject(i)}
            >
              <div className="project-img">
                <img src={p.image} alt={p.title} loading="lazy" onError={(e) => e.target.style.display = "none"} />
                <div className="project-img-overlay" />
                <div className="project-badges">
                  {p.isOpenSource && <span className="badge badge-open">Open Source</span>}
                  {p.stars && <span className="badge badge-stars"><LucideIcon name="star" size={10} /> {p.stars}</span>}
                </div>
              </div>
              <div className="project-body">
                <div className="project-date">{p.date}</div>
                <div className="project-title">{p.title}</div>
                <div className="project-desc">{p.description}</div>
                <div className="project-tags">{p.tags.map((t) => <span key={t} className="project-tag">{t}</span>)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {activeProject !== null && <ProjectModal project={projects[activeProject]} onClose={() => setActiveProject(null)} />}
    </section>
  );
}

// ─── ABOUT ────────────────────────────────────────────────────────────────────
function About() {
  return (
    <section id="about">
      <div className="container">
        <div className="section-header reveal">
          <span className="section-tag">{"// about"}</span>
          <h2 className="section-title">Who I Am</h2>
        </div>
        <div className="about-grid">
          <div>
            <div className="about-paragraphs">
              {personalInfo.aboutParagraphs.map((p, i) => (
                <p key={i} className="about-p reveal">{p}</p>
              ))}
            </div>
            <div className="about-highlights reveal reveal-delay-2">
              {personalInfo.highlights.map((h, i) => (
                <div key={i} className="highlight-item">
                  <div className="highlight-title">{h.title}</div>
                  <div className="highlight-desc">{h.description}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="about-sidebar">
            <div className="about-stats-card reveal">
              <div className="about-stats-title">{"// quick stats"}</div>
              {personalInfo.quickStats.map((s, i) => (
                <div key={i} className="about-stat-row">
                  <div className="about-stat-label">{s.label}</div>
                  <div className="about-stat-value">{s.value}</div>
                </div>
              ))}
            </div>
            <div className="values-card reveal reveal-delay-1">
              <div className="values-title">{"// core values"}</div>
              {personalInfo.coreValues.map((v, i) => (
                <div key={i} className="value-item">
                  <div className="value-bullet" />
                  {v}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── SERVICES ─────────────────────────────────────────────────────────────────
function Services() {
  return (
    <section id="services">
      <div className="container">
        <div className="section-header reveal">
          <span className="section-tag">{"// services"}</span>
          <h2 className="section-title">What I Build</h2>
          <p className="section-subtitle">End-to-end development services tailored to your needs.</p>
        </div>
        <div className="services-grid">
          {services.map((s, i) => (
            <div key={i} className={`service-card reveal reveal-delay-${(i % 2) + 1}`}>
              <div className="service-icon-wrap"><LucideIcon name={s.icon} size={22} /></div>
              <div className="service-title">{s.title}</div>
              <div className="service-desc">{s.description}</div>
              <ul className="service-features">
                {s.features.map((f, j) => <li key={j} className="service-feature">{f}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CONTACT ──────────────────────────────────────────────────────────────────
function ContactCard({ link }) {
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    if (link.copyable) {
      navigator.clipboard.writeText(link.value).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else if (link.url) {
      window.open(link.url, "_blank");
    }
  };

  return (
    <div className="contact-link-card" onClick={handleClick}>
      <div className="contact-icon"><LucideIcon name={link.icon} size={20} /></div>
      <div className="contact-info">
        <div className="contact-platform">{link.platform}</div>
        <div className="contact-value">{link.value}</div>
        <div className="contact-link-desc">{link.description}</div>
      </div>
      {copied ? (
        <div className="copy-feedback shown">Copied!</div>
      ) : (
        <div className="contact-action">{link.copyable ? "Copy" : "Visit →"}</div>
      )}
    </div>
  );
}

function Contact() {
  return (
    <section id="contact">
      <div className="container">
        <div className="contact-inner">
          <div className="reveal">
            <span className="section-tag" style={{ display: "block", textAlign: "center" }}>{"// contact"}</span>
            <h2 className="section-title">Let's Build Together</h2>
            <p className="contact-desc">Have a project in mind or want to discuss something? I'm available and responsive — just reach out through any channel below.</p>
          </div>
          <div className="contact-links reveal reveal-delay-1">
            {contactLinks.map((link) => <ContactCard key={link.platform} link={link} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer>
      <div className="container">
        <p className="footer-text">Built with <span>♥</span> by <span>{personalInfo.name}</span> — <span>{new Date().getFullYear()}</span></p>
      </div>
    </footer>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  useScrollReveal();

  return (
    <>
      <ParticleCanvas />
      <div className="grid-overlay" />
      <div className="wrapper">
        <Navbar />
        <Hero />
        <div className="glow-line" />
        <Skills />
        <div className="divider" />
        <Projects />
        <div className="divider" />
        <About />
        <div className="divider" />
        <Services />
        <div className="divider" />
        <Contact />
        <Footer />
      </div>
    </>
  );
}
